import { CopyIcon } from "@xola/icons";
import { Button, DatePickerPopover, Label, Modal, SubmitButton, Switch, Table } from "@xola/ui-kit";
import { DateFormat, formatDate, now } from "common";
import dayjs from "dayjs";
import { get } from "lodash";
import React, { memo, useCallback, useMemo, useState } from "react";
import { OpeningHour, OpeningHoursPerDay, Weekday, WeekdayCopy } from "types";
import { ConfirmLeaveModal } from "@/components/Common/ConfirmLeaveModal";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { TimeslotTimePicker } from "@/components/PurchaseModification/TimeslotTimePicker";
import { useModal } from "@/hooks/useVisibility";

const DAY_ORDER: Weekday[] = [
    Weekday.MONDAY,
    Weekday.TUESDAY,
    Weekday.WEDNESDAY,
    Weekday.THURSDAY,
    Weekday.FRIDAY,
    Weekday.SATURDAY,
    Weekday.SUNDAY,
];

interface OpeningHoursModalProps {
    isOpen: boolean;
    initialValue?: OpeningHour;
    siblings: OpeningHour[];
    onClose: () => void;
    onApply: (entry: OpeningHour) => Promise<void>;
}

export const OpeningHoursModal = ({ isOpen, initialValue, siblings, onClose, onApply }: OpeningHoursModalProps) => {
    if (!isOpen) {
        return null;
    }

    return (
        <Modal isOpen size="medium" className="!max-w-[470px] !p-8" onClose={onClose}>
            <OpeningHoursModalBody
                initialValue={initialValue}
                siblings={siblings}
                onClose={onClose}
                onApply={onApply}
            />
        </Modal>
    );
};

interface BodyProps {
    initialValue?: OpeningHour;
    siblings: OpeningHour[];
    onClose: () => void;
    onApply: (entry: OpeningHour) => Promise<void>;
}

const buildInitialEntry = (initialValue?: OpeningHour): OpeningHour => {
    if (initialValue) {
        return {
            ...initialValue,
            openingHoursPerDay: (initialValue.openingHoursPerDay ?? []).map((dayHours) => ({
                ...dayHours,
                timeRange: { ...dayHours.timeRange },
            })),
        };
    }
    return {
        name: "",
        start: formatDate(now(), DateFormat.DATE_ISO),
        end: null,
        openingHoursPerDay: [],
    };
};

const POPOVER_PROPS = { appendTo: () => document.body, zIndex: 40, placement: "auto" } as const;

const validateEntry = (values: OpeningHour, siblings: OpeningHour[]): Record<string, string> => {
    const errors: Record<string, string> = {};
    // Double equals used intentionally to allow both null and undefined for end date
    const noEnd = values.end == null;

    if (!values.start) {
        errors.start = "Start date is required.";
    }
    if (!noEnd && !values.end) {
        errors.end = "End date is required, or enable 'No end'.";
    }
    if (!noEnd && values.end && now(values.end).isBefore(now(values.start), "day")) {
        errors.end = "End date must be on or after the start date.";
    }
    if (values.openingHoursPerDay.length === 0) {
        errors.openingHoursPerDay = "Select at least one working day.";
    } else {
        for (const dayHours of values.openingHoursPerDay) {
            const { startTime, endTime } = dayHours.timeRange;
            if (startTime === null || endTime === null) {
                errors.openingHoursPerDay = `Set business hours for ${DAY_LABELS[dayHours.day]}.`;
                break;
            }
            if (startTime >= endTime) {
                errors.openingHoursPerDay = `${DAY_LABELS[dayHours.day]}: end time must be after start time.`;
                break;
            }
        }
    }

    if (
        !errors.start &&
        !errors.end &&
        overlapsAny({ id: values.id, start: values.start, end: values.end }, siblings)
    ) {
        errors.overlap = "Date range overlaps another business hours entry.";
    }

    return errors;
};

const OpeningHoursModalBody = ({ initialValue, siblings, onClose, onApply }: BodyProps) => {
    const initialEntry = useMemo(() => buildInitialEntry(initialValue), [initialValue]);
    const [serverError, setServerError] = useState<string | null>(null);
    const confirmLeaveModal = useModal(false);

    const validate = (values: OpeningHour) => validateEntry(values, siblings);

    const handleFormSubmit = async (values: OpeningHour) => {
        setServerError(null);
        try {
            await onApply(values);
        } catch (error) {
            const status = get(error, "response.status");
            if (status === 409) {
                setServerError("Date range overlaps another business hours entry.");
            } else {
                setServerError("An unexpected error occurred. Please try again.");
            }
        }
    };

    return (
        <XolaFormik<OpeningHour>
            enablePrompt={false}
            initialValues={initialEntry}
            validate={validate}
            onSubmit={handleFormSubmit}
        >
            {({ values, setValues, errors, handleSubmit, isSubmitting, submitCount, dirty }) => {
                const noEnd = values.end == null;
                const firstError = submitCount > 0 ? Object.values(errors)[0] : null;
                const displayError = serverError ?? firstError ?? null;

                const sortedDayHours = [...values.openingHoursPerDay].sort(
                    (a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day),
                );
                const selectedDays = new Set(values.openingHoursPerDay.map((dayHours) => dayHours.day));

                const handleStartDateChange = (date: Date | dayjs.Dayjs | null) => {
                    if (!date) {
                        return;
                    }
                    const start = formatDate(date, DateFormat.DATE_ISO);
                    setValues((previous) => ({
                        ...previous,
                        start,
                        end: previous.end && now(previous.end).isBefore(now(start), "day") ? start : previous.end,
                    }));
                };

                const handleEndDateChange = (date: Date | dayjs.Dayjs | null) => {
                    setValues((previous) => ({
                        ...previous,
                        end: date ? formatDate(date, DateFormat.DATE_ISO) : null,
                    }));
                };

                const handleNoEndToggle = (checked: boolean) => {
                    setValues((previous) => ({ ...previous, end: checked ? null : previous.start }));
                };

                const toggleDay = (day: Weekday) => {
                    setValues((previous) => {
                        const isSelected = previous.openingHoursPerDay.some((dayHours) => dayHours.day === day);
                        if (isSelected) {
                            return {
                                ...previous,
                                openingHoursPerDay: previous.openingHoursPerDay.filter(
                                    (dayHours) => dayHours.day !== day,
                                ),
                            };
                        }
                        return {
                            ...previous,
                            openingHoursPerDay: [
                                ...previous.openingHoursPerDay,
                                { day, timeRange: { startTime: null, endTime: null } },
                            ],
                        };
                    });
                };

                const updateHour = (day: Weekday, field: "startTime" | "endTime", value: number) => {
                    setValues((previous) => ({
                        ...previous,
                        openingHoursPerDay: previous.openingHoursPerDay.map((dayHours) =>
                            dayHours.day === day
                                ? { ...dayHours, timeRange: { ...dayHours.timeRange, [field]: value } }
                                : dayHours,
                        ),
                    }));
                };

                const copyToAll = (sourceDay: Weekday) => {
                    setValues((previous) => {
                        const source = previous.openingHoursPerDay.find((dayHours) => dayHours.day === sourceDay);
                        if (!source) {
                            return previous;
                        }
                        return {
                            ...previous,
                            openingHoursPerDay: previous.openingHoursPerDay.map((dayHours) => ({
                                day: dayHours.day,
                                timeRange: { ...source.timeRange },
                            })),
                        };
                    });
                };

                const startDateValue = values.start ? now(values.start).toDate() : null;
                const endDateValue = noEnd || !values.end ? null : now(values.end).toDate();
                const endDateMonth = values.end || values.start ? now(values.end ?? values.start).toDate() : null;

                const isStartDateDisabled = (date: Date | dayjs.Dayjs) => {
                    // Always allow the currently-saved start date so editing existing
                    // schedules with historical dates doesn't disable the calendar cell.
                    const isOriginalStartDate =
                        !!initialValue?.start && now(date).isSame(now(initialValue.start), "day");
                    if (isOriginalStartDate) return false;
                    const isInPast = now(date).isBefore(now().subtract(1, "day"));
                    const isAfterEndDate = !!values.end && now(date).isAfter(now(values.end).endOf("day"));
                    return isInPast || isAfterEndDate;
                };

                const endDateDisabledDays = values.start
                    ? [{ before: now(values.start).toDate() }]
                    : [{ before: now().subtract(1, "day").toDate() }];

                return (
                    <>
                        <Modal.Header>Business Hours</Modal.Header>
                        <Modal.Body>
                            <div className="flex flex-col space-y-5">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label>Start Date</Label>
                                        <DatePickerPopover
                                            value={startDateValue}
                                            popoverProps={POPOVER_PROPS}
                                            disabledDays={isStartDateDisabled}
                                            month={startDateValue}
                                            dateFormat={DateFormat.DATE_LONG}
                                            classNames={{ input: "!pl-8" }}
                                            onChange={handleStartDateChange}
                                        />
                                    </div>
                                    <div>
                                        <Label>End Date</Label>
                                        <DatePickerPopover
                                            value={endDateValue}
                                            popoverProps={POPOVER_PROPS}
                                            disabledDays={endDateDisabledDays}
                                            month={endDateMonth}
                                            dateFormat={DateFormat.DATE_LONG}
                                            classNames={{ input: "!pl-8" }}
                                            onChange={handleEndDateChange}
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end pt-1">
                                    <div className="flex items-center space-x-2">
                                        <Switch size="medium" isChecked={noEnd} onChange={handleNoEndToggle} />
                                        <span>No end</span>
                                    </div>
                                </div>

                                <div className="flex flex-col space-y-2">
                                    <Label>Working Days</Label>
                                    <div className="flex items-center space-x-1">
                                        {DAY_ORDER.map((day) => {
                                            const selected = selectedDays.has(day);
                                            if (selected) {
                                                return (
                                                    <Button
                                                        key={day}
                                                        className="w-14 bg-primary-lighter text-black hover:bg-primary-lighter focus:ring-0"
                                                        color="primary"
                                                        variant="outline"
                                                        size="small"
                                                        onClick={() => toggleDay(day)}
                                                    >
                                                        {WeekdayCopy[day]}
                                                    </Button>
                                                );
                                            }
                                            return (
                                                <SecondaryButton
                                                    key={day}
                                                    className="w-14 text-black focus:ring-0"
                                                    size="small"
                                                    onClick={() => toggleDay(day)}
                                                >
                                                    {WeekdayCopy[day]}
                                                </SecondaryButton>
                                            );
                                        })}
                                    </div>
                                </div>

                                <DayHoursTable
                                    sortedDayHours={sortedDayHours}
                                    onUpdateHour={updateHour}
                                    onCopyToAll={copyToAll}
                                />

                                {displayError ? (
                                    <div className="text-sm text-danger" data-testid="business-hours-modal-error">
                                        {displayError}
                                    </div>
                                ) : null}
                            </div>
                        </Modal.Body>
                        <Modal.Footer className="space-x-4">
                            <ConfirmLeaveModal
                                disableRouterPrompt
                                copy="You have unsaved changes. Are you sure you want to discard them?"
                                manualShowPrompt={confirmLeaveModal.isVisible}
                                handleConfirm={onClose}
                                handleCancel={confirmLeaveModal.hide}
                            />
                            <SecondaryButton onClick={() => (dirty ? confirmLeaveModal.show() : onClose())}>
                                Cancel
                            </SecondaryButton>
                            <SubmitButton disabled={isSubmitting} isLoading={isSubmitting} onClick={handleSubmit}>
                                Save
                            </SubmitButton>
                        </Modal.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};

interface DayHoursTableProps {
    sortedDayHours: OpeningHoursPerDay[];
    onUpdateHour: (day: Weekday, field: "startTime" | "endTime", value: number) => void;
    onCopyToAll: (sourceDay: Weekday) => void;
}

const DayHoursTable = memo(({ sortedDayHours, onUpdateHour, onCopyToAll }: DayHoursTableProps) => {
    if (sortedDayHours.length === 0) {
        return null;
    }

    return (
        <div className="overflow-hidden rounded border border-gray-light">
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header className="!text-sm">Day</Table.Header>
                        <Table.Header className="!text-sm">Business Hours</Table.Header>
                        <Table.Header className="w-12 !text-sm" />
                    </Table.Row>
                </Table.Head>

                <Table.Body>
                    {sortedDayHours.map((dayHours, index) => (
                        <DayHoursRow
                            key={dayHours.day}
                            dayHours={dayHours}
                            showCopyToAll={index === 0 && sortedDayHours.length > 1}
                            onUpdateHour={onUpdateHour}
                            onCopyToAll={onCopyToAll}
                        />
                    ))}
                </Table.Body>
            </Table>
        </div>
    );
});

DayHoursTable.displayName = "DayHoursTable";

interface DayHoursRowProps {
    dayHours: OpeningHoursPerDay;
    showCopyToAll: boolean;
    onUpdateHour: (day: Weekday, field: "startTime" | "endTime", value: number) => void;
    onCopyToAll: (sourceDay: Weekday) => void;
}

const DayHoursRow = memo(({ dayHours, showCopyToAll, onUpdateHour, onCopyToAll }: DayHoursRowProps) => {
    const handleStartChange = useCallback(
        (value: number) => onUpdateHour(dayHours.day, "startTime", value),
        [dayHours.day, onUpdateHour],
    );
    const handleEndChange = useCallback(
        (value: number) => onUpdateHour(dayHours.day, "endTime", value),
        [dayHours.day, onUpdateHour],
    );
    const handleCopyClick = useCallback(() => onCopyToAll(dayHours.day), [dayHours.day, onCopyToAll]);

    return (
        <Table.Row>
            <Table.Cell className="pl-4 pr-2 align-middle">{DAY_LABELS[dayHours.day]}</Table.Cell>
            <Table.Cell className="!px-2 align-middle">
                <div className="flex items-center space-x-1.5">
                    <TimeslotTimePicker
                        isMinimal
                        className="w-20"
                        placeholder="Set time"
                        value={dayHours.timeRange.startTime ?? null}
                        onChange={handleStartChange}
                    />
                    <span>to</span>
                    <TimeslotTimePicker
                        isMinimal
                        className="w-20"
                        placeholder="Set time"
                        value={dayHours.timeRange.endTime ?? null}
                        onChange={handleEndChange}
                    />
                </div>
            </Table.Cell>
            <Table.Cell className="whitespace-nowrap !px-2 align-middle">
                {showCopyToAll ? (
                    <button
                        type="button"
                        aria-label="Copy hours to all days"
                        className="inline-flex items-center gap-1 rounded text-sm hover:text-primary"
                        onClick={handleCopyClick}
                    >
                        <CopyIcon className="h-4 w-4" />
                        <span className="underline underline-offset-2">Copy to all</span>
                    </button>
                ) : null}
            </Table.Cell>
        </Table.Row>
    );
});

DayHoursRow.displayName = "DayHoursRow";

export const DAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

export const overlapsAny = (entry: Pick<OpeningHour, "id" | "start" | "end">, others: OpeningHour[]): boolean => {
    const entryStart = now(entry.start).startOf("day");
    const entryEnd = entry.end ? now(entry.end).endOf("day") : null;

    return others.some((other) => {
        if (other.id && entry.id && other.id === entry.id) {
            return false;
        }
        const otherStart = now(other.start).startOf("day");
        const otherEnd = other.end ? now(other.end).endOf("day") : null;

        const entryStartsAfterOtherEnds = otherEnd !== null && entryStart.isAfter(otherEnd);
        const otherStartsAfterEntryEnds = entryEnd !== null && otherStart.isAfter(entryEnd);
        return !(entryStartsAfterOtherEnds || otherStartsAfterEntryEnds);
    });
};
